import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FaEdit, FaTrash } from 'react-icons/fa';
import adminSchemaApi from '../api/adminSchemaApi';
import { formatCurrency, formatCurrencyLabel } from '../../utils/formatUtils';
import '../styles/admin-theme.css';

const AdminPlanDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [plan, setPlan] = useState(null);
  const [entries, setEntries] = useState([]);
  const [pricebooks, setPricebooks] = useState([]);
  const [planLicenses, setPlanLicenses] = useState([]);
  const [licenseTypes, setLicenseTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [showEntryModal, setShowEntryModal] = useState(false);
  const [entryModalError, setEntryModalError] = useState('');
  const [editingEntry, setEditingEntry] = useState(null);
  const [entryForm, setEntryForm] = useState({
    pricebook_id: '',
    productid: '',
    list_price: '',
    discount_percent: '',
    tax_percent: '',
    is_active: true,
  });

  const loadPlan = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const data = await adminSchemaApi.fetchPlanById(id);
      setPlan(data);
    } catch (err) {
      setError(err?.message || 'Failed to load plan');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadEntries = useCallback(async () => {
    if (!id) return;
    setEntriesLoading(true);
    try {
      const list = await adminSchemaApi.fetchPlanPricebookEntries(id);
      setEntries(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err?.message || 'Failed to load prices');
    } finally {
      setEntriesLoading(false);
    }
  }, [id]);

  const loadPricebooks = useCallback(async () => {
    try {
      const list = await adminSchemaApi.fetchPricebooks();
      setPricebooks(Array.isArray(list) ? list : []);
    } catch {
      setPricebooks([]);
    }
  }, []);

  const loadPlanLicenses = useCallback(async () => {
    if (!id) return;
    try {
      const list = await adminSchemaApi.fetchPlanLicenses(id);
      setPlanLicenses(Array.isArray(list) ? list : []);
    } catch {
      setPlanLicenses([]);
    }
  }, [id]);

  const loadLicenseTypes = useCallback(async () => {
    try {
      const list = await adminSchemaApi.fetchLicenseTypes();
      setLicenseTypes(Array.isArray(list) ? list : []);
    } catch {
      setLicenseTypes([]);
    }
  }, []);

  useEffect(() => {
    if (!id) return;
    loadPlan();
  }, [id, loadPlan]);

  useEffect(() => {
    if (plan) loadEntries();
  }, [plan, loadEntries]);

  useEffect(() => {
    loadPricebooks();
    loadPlanLicenses();
    loadLicenseTypes();
  }, [loadPricebooks, loadPlanLicenses, loadLicenseTypes]);

  const availableLicenseTypes = entryForm.pricebook_id
    ? licenseTypes.filter((lt) =>
        planLicenses.some((pl) => pl.license_type_id === lt.id) ||
        (editingEntry && lt.id === entryForm.productid)
      )
    : [];

  const openEntryModal = (entry = null) => {
    setEntryModalError('');
    setEditingEntry(entry);
    if (entry) {
      setEntryForm({
        pricebook_id: entry.pricebookid,
        productid: entry.productid || '',
        list_price: entry.list_price != null ? entry.list_price : '',
        discount_percent: entry.discount_percent != null ? entry.discount_percent : '',
        tax_percent: entry.tax_percent != null ? entry.tax_percent : '',
        is_active: !!entry.is_active,
      });
    } else {
      setEntryForm({
        pricebook_id: '',
        productid: '',
        list_price: '',
        discount_percent: '',
        tax_percent: '',
        is_active: true,
      });
    }
    setShowEntryModal(true);
  };

  const handleSaveEntry = async (e) => {
    e.preventDefault();
    if (!id) return;
    if (!entryForm.pricebook_id || !entryForm.productid) {
      setEntryModalError('Pricebook and license type are required.');
      return;
    }
    setSaving(true);
    setEntryModalError('');
    try {
      const body = {
        object_name: 'license_type',
        productid: entryForm.productid,
        plan_id: id,
        list_price: entryForm.list_price === '' ? null : Number(entryForm.list_price),
        discount_percent: entryForm.discount_percent === '' ? null : Number(entryForm.discount_percent),
        tax_percent: entryForm.tax_percent === '' ? null : Number(entryForm.tax_percent),
        is_active: entryForm.is_active,
      };
      if (editingEntry) {
        await adminSchemaApi.updatePricebookEntry(
          entryForm.pricebook_id,
          editingEntry.id,
          body
        );
      } else {
        await adminSchemaApi.createPricebookEntry(entryForm.pricebook_id, body);
      }
      setShowEntryModal(false);
      loadEntries();
    } catch (err) {
      setEntryModalError(err?.message || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async (entry) => {
    if (!window.confirm('Remove this price entry?')) return;
    setSaving(true);
    setError('');
    try {
      await adminSchemaApi.deletePricebookEntry(entry.pricebookid, entry.id);
      loadEntries();
    } catch (err) {
      setError(err?.message || 'Failed to delete entry');
    } finally {
      setSaving(false);
    }
  };

  if (!id) {
    navigate('/admin/plans', { replace: true });
    return null;
  }

  if (loading || !plan) {
    return (
      <>
        <Link to="/admin/plans" className="btn btn-outline-secondary btn-sm mb-3">← Plans</Link>
        <p className="admin-subtitle">{loading ? 'Loading…' : 'Plan not found.'}</p>
      </>
    );
  }

  return (
    <>
      <div className="mb-3">
        <Link to="/admin/plans" className="btn btn-outline-secondary btn-sm me-2">← Plans</Link>
        <Link to={`/admin/plans/${id}`} className="btn btn-outline-primary btn-sm me-2">Configure</Link>
        <Link to={`/admin/plans/${id}/licenses`} className="btn btn-outline-info btn-sm me-2">Licenses</Link>
      </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      <Row className="g-3">
        <Col md={5}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <Card.Title as="h5" className="mb-0">Plan prices (by pricebook)</Card.Title>
              <Button size="sm" className="admin-primary-btn" onClick={() => openEntryModal(null)}>
                + Add price
              </Button>
            </Card.Header>
            <Card.Body>
              <p className="admin-subtitle small mb-2">
                Prices for this plan across pricebooks. Add or edit entries per pricebook and license type.
              </p>
              {entriesLoading ? (
                <p className="admin-subtitle small">Loading…</p>
              ) : (
                <Table striped bordered hover size="sm" responsive>
                  <thead>
                    <tr>
                      <th>Pricebook</th>
                      <th>License type</th>
                      <th>List price</th>
                      <th>Discount %</th>
                      <th style={{ width: 90 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry) => (
                      <tr key={entry.id}>
                        <td>{entry.pricebook_name || '—'}</td>
                        <td>{entry.license_type_name || entry.license_type_code || '—'}</td>
                        <td>
                          {entry.list_price != null
                            ? formatCurrency(entry.list_price, entry.pricebook_currency)
                            : '—'}
                        </td>
                        <td>{entry.discount_percent != null ? Number(entry.discount_percent) : '—'}</td>
                        <td>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-1"
                            onClick={() => openEntryModal(entry)}
                          >
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteEntry(entry)}
                            disabled={saving}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {entries.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center text-muted">
                          No prices yet. Add entries per pricebook and license type.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={7}>
          <Card>
            <Card.Header>
              <Card.Title as="h5" className="mb-0">Plan details</Card.Title>
            </Card.Header>
            <Card.Body>
              <dl className="row mb-0">
                <dt className="col-sm-3">Code</dt>
                <dd className="col-sm-9">{plan.code || '—'}</dd>

                <dt className="col-sm-3">Name</dt>
                <dd className="col-sm-9">{plan.name || '—'}</dd>

                <dt className="col-sm-3">Description</dt>
                <dd className="col-sm-9">{plan.description || '—'}</dd>

                <dt className="col-sm-3">Active</dt>
                <dd className="col-sm-9">{plan.is_active ? 'Yes' : 'No'}</dd>
              </dl>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showEntryModal} onHide={() => { setShowEntryModal(false); setEntryModalError(''); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingEntry ? 'Edit price entry' : 'Add price entry'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSaveEntry}>
          <Modal.Body>
            {entryModalError && (
              <Alert variant="danger" dismissible onClose={() => setEntryModalError('')} className="mb-3">
                {entryModalError}
              </Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Pricebook</Form.Label>
              <Form.Select
                value={entryForm.pricebook_id}
                onChange={(e) => setEntryForm((p) => ({ ...p, pricebook_id: e.target.value, productid: '' }))}
                required
                disabled={!!editingEntry}
              >
                <option value="">— Select pricebook —</option>
                {pricebooks.filter((pb) => pb.is_active).map((pb) => (
                  <option key={pb.id} value={pb.id}>
                    {pb.name || pb.id} ({pb.currency ? formatCurrencyLabel(pb.currency) : '-'})
                  </option>
                ))}
              </Form.Select>
              {editingEntry && <Form.Text className="text-muted">Pricebook cannot be changed when editing.</Form.Text>}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>License type</Form.Label>
              <Form.Select
                value={entryForm.productid}
                onChange={(e) => setEntryForm((p) => ({ ...p, productid: e.target.value }))}
                required
                disabled={!entryForm.pricebook_id}
              >
                <option value="">— {entryForm.pricebook_id ? 'Select license type' : 'Select pricebook first'} —</option>
                {availableLicenseTypes.map((lt) => (
                  <option key={lt.id} value={lt.id}>
                    {lt.name || lt.code} ({lt.code})
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">License types from this plan's licenses.</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>List price</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={entryForm.list_price}
                onChange={(e) => setEntryForm((p) => ({ ...p, list_price: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Discount %</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={entryForm.discount_percent}
                onChange={(e) => setEntryForm((p) => ({ ...p, discount_percent: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Tax %</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={entryForm.tax_percent}
                onChange={(e) => setEntryForm((p) => ({ ...p, tax_percent: e.target.value }))}
              />
            </Form.Group>
            <Form.Check
              type="checkbox"
              label="Active"
              checked={entryForm.is_active}
              onChange={(e) => setEntryForm((p) => ({ ...p, is_active: e.target.checked }))}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowEntryModal(false)}>Cancel</Button>
            <Button type="submit" className="admin-primary-btn" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default AdminPlanDetail;
